import {
	ConstructionSiteDocument,
	PublicConstructionSiteDetailsDto,
	UploadConstructionSiteDocumentData,
} from "@dune/types";
import { getErrorMessage } from "@dune/vars";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { privateAxios, publicAxios } from "../axios";
import { constructionSitesQueryKeys } from "../query-keys/constructionSites.query-keys";

export const uploadConstructionSiteDocument = async (
	data: UploadConstructionSiteDocumentData,
): Promise<ConstructionSiteDocument> => {
	const { constructionSiteId, ...documentData } = data;
	const response = await privateAxios.post<ConstructionSiteDocument>(
		`/constructionSites/${constructionSiteId}/documents`,
		documentData,
	);
	return response.data;
};

export const deleteConstructionSiteDocument = async (
	constructionSiteId: string,
	documentId: string,
): Promise<void> => {
	await privateAxios.delete(
		`/constructionSites/${constructionSiteId}/documents/${documentId}`,
	);
};

export const useUploadConstructionSiteDocumentMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: uploadConstructionSiteDocument,
		onSuccess: (_, variables) => {
			toast.success("Document uploadé avec succès");
			queryClient.invalidateQueries({
				queryKey: [
					...constructionSitesQueryKeys.all,
					"documents",
					variables.constructionSiteId,
				],
			});
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'upload du document", {
				description: errorMessage,
			});
		},
	});
};

export const useDeleteConstructionSiteDocumentMutation = (
	constructionSiteId: string,
	options?: {
		onMutate?: (documentId: string) => void;
		onSettled?: () => void;
	},
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (documentId: string) =>
			deleteConstructionSiteDocument(constructionSiteId, documentId),
		onMutate: options?.onMutate,
		onSuccess: () => {
			queryClient.invalidateQueries({
				predicate: (query) =>
					query.queryKey[0] === "construction-sites" &&
					query.queryKey[1] === "documents" &&
					query.queryKey[2] === Number(constructionSiteId),
			});
			queryClient.invalidateQueries({
				queryKey: constructionSitesQueryKeys.detail(constructionSiteId),
			});
			toast.success("Document supprimé avec succès");
		},
		onError: () => {
			toast.error("Erreur lors de la suppression du document");
		},
		onSettled: options?.onSettled,
	});
};

// Mutations pour le questionnaire public
export const submitPublicConstructionSiteQuestionnaire = async (
	constructionSiteId: number,
	token: string,
	questionnaireData: PublicConstructionSiteDetailsDto,
): Promise<{ success: boolean; message: string }> => {
	const response = await publicAxios.put(
		`/constructionSites/${constructionSiteId}/submit?token=${token}`,
		questionnaireData,
	);
	return response.data;
};

export const useSubmitPublicConstructionSiteQuestionnaire = () => {
	return useMutation({
		mutationFn: ({
			constructionSiteId,
			token,
			questionnaireData,
		}: {
			constructionSiteId: number;
			token: string;
			questionnaireData: PublicConstructionSiteDetailsDto;
		}) =>
			submitPublicConstructionSiteQuestionnaire(
				constructionSiteId,
				token,
				questionnaireData,
			),
		onSuccess: () => {
			toast.success("Questionnaire soumis avec succès");
		},
		onError: (error) => {
			console.error("Erreur lors de la soumission:", error);
			toast.error("Erreur lors de la soumission du questionnaire");
		},
	});
};
